/**
 * Licence-allocation analysis (C11). Pure, deterministic.
 *
 * Detects two waste/conflict patterns per user (per the chosen definition):
 *   1. Overlapping service plans — the same underlying service plan is granted
 *      (enabled) by two or more assigned SKUs. Duplicated entitlement.
 *   2. Redundant SKUs — a SKU whose enabled service plans are a subset of another
 *      assigned SKU's enabled plans adds nothing and is redundant.
 */
import type { SubscribedSku } from '@/graph/types';
import {
  LICENSE_ENDPOINTS,
  getLicensedUsers,
  getSubscribedSkusForLicensing,
  type LicensedUser,
} from '@/graph/modules/licenses';

export interface PlanOverlap {
  planName: string;
  skus: string[]; // SKU part numbers granting this plan (enabled)
}
export interface RedundantSku {
  redundant: string; // SKU part number that adds nothing
  coveredBy: string; // SKU part number that already covers it
}
export interface LicenseUserFinding {
  id: string;
  displayName: string;
  userPrincipalName: string;
  skus: string[];
  overlaps: PlanOverlap[];
  redundant: RedundantSku[];
}
export interface LicenseSummary {
  totalLicensedUsers: number;
  usersWithOverlap: number;
  usersWithRedundant: number;
  wastedAssignments: number; // duplicated plan-grants (each extra grant beyond the first)
  topPlans: { name: string; users: number }[];
}
export interface LicenseResult extends LicenseSummary {
  findings: LicenseUserFinding[];
}

interface SkuInfo {
  partNumber: string;
  plans: Map<string, string>; // servicePlanId -> servicePlanName
}

function indexSkus(skus: SubscribedSku[]): Map<string, SkuInfo> {
  const map = new Map<string, SkuInfo>();
  for (const s of skus) {
    if (!s.skuId) continue;
    const plans = new Map<string, string>();
    for (const p of s.servicePlans ?? []) {
      if (p.servicePlanId) plans.set(p.servicePlanId, p.servicePlanName ?? p.servicePlanId);
    }
    map.set(s.skuId, { partNumber: s.skuPartNumber ?? s.skuId, plans });
  }
  return map;
}

export function evaluateLicenses(users: LicensedUser[], skus: SubscribedSku[]): LicenseResult {
  const skuIndex = indexSkus(skus);
  const findings: LicenseUserFinding[] = [];
  const planUserCounts = new Map<string, number>();
  let wastedAssignments = 0;

  for (const u of users) {
    const assigned = (u.assignedLicenses ?? []).filter((a) => a.skuId && skuIndex.has(a.skuId));
    if (assigned.length === 0) continue;

    // enabled plans per assigned SKU (part number + set of enabled plan ids/names)
    const perSku = assigned.map((a) => {
      const info = skuIndex.get(a.skuId as string) as SkuInfo;
      const disabled = new Set(a.disabledPlans ?? []);
      const enabled = new Map<string, string>();
      for (const [planId, planName] of info.plans) {
        if (!disabled.has(planId)) enabled.set(planId, planName);
      }
      return { partNumber: info.partNumber, enabled };
    });

    // 1. Overlapping service plans: plan enabled by >= 2 SKUs.
    const planToSkus = new Map<string, { name: string; skus: Set<string> }>();
    for (const sku of perSku) {
      for (const [planId, planName] of sku.enabled) {
        let entry = planToSkus.get(planId);
        if (!entry) {
          entry = { name: planName, skus: new Set() };
          planToSkus.set(planId, entry);
        }
        entry.skus.add(sku.partNumber);
      }
    }
    const overlaps: PlanOverlap[] = [];
    for (const entry of planToSkus.values()) {
      if (entry.skus.size >= 2) {
        overlaps.push({ planName: entry.name, skus: [...entry.skus].sort() });
        wastedAssignments += entry.skus.size - 1;
        planUserCounts.set(entry.name, (planUserCounts.get(entry.name) ?? 0) + 1);
      }
    }

    // 2. Redundant SKUs: enabled(X) ⊆ enabled(Y), X != Y. Dedupe equal sets by name.
    const redundant: RedundantSku[] = [];
    for (let i = 0; i < perSku.length; i++) {
      for (let j = 0; j < perSku.length; j++) {
        if (i === j) continue;
        const x = perSku[i];
        const y = perSku[j];
        if (!x || !y || x.enabled.size === 0) continue;
        const subset = [...x.enabled.keys()].every((k) => y.enabled.has(k));
        if (!subset) continue;
        // If sets are equal, only report the lexicographically-larger as redundant (dedupe).
        if (x.enabled.size === y.enabled.size && x.partNumber <= y.partNumber) continue;
        redundant.push({ redundant: x.partNumber, coveredBy: y.partNumber });
      }
    }

    if (overlaps.length > 0 || redundant.length > 0) {
      findings.push({
        id: u.id,
        displayName: u.displayName ?? u.userPrincipalName ?? u.id,
        userPrincipalName: u.userPrincipalName ?? '',
        skus: perSku.map((s) => s.partNumber).sort(),
        overlaps: overlaps.sort((a, b) => a.planName.localeCompare(b.planName)),
        redundant,
      });
    }
  }

  findings.sort(
    (a, b) => b.overlaps.length + b.redundant.length - (a.overlaps.length + a.redundant.length),
  );

  const topPlans = [...planUserCounts.entries()]
    .map(([name, users]) => ({ name, users }))
    .sort((a, b) => b.users - a.users)
    .slice(0, 6);

  const totalLicensedUsers = users.filter((u) => (u.assignedLicenses ?? []).length > 0).length;

  return {
    totalLicensedUsers,
    usersWithOverlap: findings.filter((f) => f.overlaps.length > 0).length,
    usersWithRedundant: findings.filter((f) => f.redundant.length > 0).length,
    wastedAssignments,
    topPlans,
    findings,
  };
}

export const LICENSE_ENDPOINTS_USED = [LICENSE_ENDPOINTS.users, LICENSE_ENDPOINTS.subscribedSkus];

export async function auditLicenses(): Promise<LicenseResult> {
  const [users, skus] = await Promise.all([
    getLicensedUsers(),
    getSubscribedSkusForLicensing(),
  ]);
  return evaluateLicenses(users, skus);
}
